<script setup>
import { computed, ref, watch, useSlots, useAttrs } from 'vue'
import { DateTime, Info } from 'luxon'

// V-Model (kompatibilní s v-calendar)
const modelValue = defineModel()

const props = defineProps({
  // Rozšíření tohoto kalendáře
  priceMap: { type: Object, default: () => ({}) }, // { 'YYYY-MM-DD': { amount, currency?, color?, note? } }
  defaultCurrency: { type: String, default: 'CZK' },
  enableWeekToggle: { type: Boolean, default: true },
  initialView: { type: String, default: 'month' }, // 'month' | 'week'

  // API kompatibilní s v-calendar (subset, rozšiřitelný)
  isRange: { type: Boolean, default: false },
  columns: { type: Number, default: 1 }, // 2 = 2 měsíce vedle sebe
  color: { type: String, default: 'blue' },
  locale: { type: [String, Object], default: 'cs' },
  masks: { type: Object, default: undefined }, // rezervováno (např. { title: 'LLLL yyyy' })
  minDate: { type: [String, Date, Object], default: undefined },
  maxDate: { type: [String, Date, Object], default: undefined },
  disabledDates: { type: [Array, Object, Function], default: undefined },
  availableDates: { type: [Array, Object, Function], default: undefined },
  firstDayOfWeek: { type: Number, default: 1 }, // 1 = Monday
  titlePosition: { type: String, default: 'center' }, // 'left' | 'center' | 'right'
  view: { type: String, default: undefined }, // 'month' | 'week' (má přednost před initialView)
  attributes: { type: Array, default: () => [] }, // kompatibilní signatura

  // Start zobrazení – od kterého měsíce (ISO string nebo Date)
  initialMonth: { type: [String, Date, Object], default: undefined },
})

const emit = defineEmits(['update:modelValue', 'dayclick', 'daymouseenter', 'daymouseleave'])
const slots = useSlots()
const attrs = useAttrs()

// Interní stav
const today = DateTime.local()
const localeResolved = computed(() => (typeof props.locale === 'string' ? props.locale : 'cs'))
const monthTitleMask = computed(() => props?.masks?.title ?? 'LLLL yyyy')

// Počáteční ukazatel měsíce
const baseMonth = ref(
  props.initialMonth
    ? toDateTime(props.initialMonth).startOf('month')
    : today.startOf('month')
)

// View (month|week)
const internalView = ref(props.initialView === 'week' ? 'week' : 'month')
watch(() => props.view, (now) => {
  if (now === 'week' || now === 'month') {
    internalView.value = now
  }
})
const effectiveView = computed(() => (props.view ? props.view : internalView.value))

// Výběr (single/range)
const selection = computed({
  get: () => modelValue.value,
  set: (val) => emit('update:modelValue', val),
})

// Helpers
function toDateTime(input) {
  if (!input) {
    return null
  }
  if (DateTime.isDateTime(input)) {
    return input
  }
  if (typeof input === 'string') {
    // Podpora ISO
    const dt = DateTime.fromISO(input)
    return dt.isValid ? dt : null
  }
  if (input instanceof Date) {
    return DateTime.fromJSDate(input)
  }
  if (typeof input === 'object' && input.year && input.month && input.day) {
    return DateTime.fromObject(input)
  }
  return null
}

function clampToRange(dt) {
  if (!dt) { return null }
  const min = toDateTime(props.minDate)
  const max = toDateTime(props.maxDate)
  let result = dt
  if (min && dt < min.startOf('day')) {
    result = min
  }
  if (max && dt > max.endOf('day')) {
    result = max
  }
  return result
}

// Disabled/Available resolve (subset kompatibility)
function isDisabledDate(dt) {
  if (!dt) { return true }
  // min/max
  const min = toDateTime(props.minDate)
  const max = toDateTime(props.maxDate)
  if (min && dt < min.startOf('day')) { return true }
  if (max && dt > max.endOf('day')) { return true }

  const d = dt.toISODate()

  // disabledDates: Function | { start,end } | Array<string|{start,end}>
  if (typeof props.disabledDates === 'function') {
    if (props.disabledDates(dt.toJSDate())) { return true }
  } else if (Array.isArray(props.disabledDates)) {
    for (const item of props.disabledDates) {
      if (typeof item === 'string') {
        if (d === item) { return true }
      } else if (item?.start || item?.end) {
        const s = toDateTime(item.start)?.startOf('day')
        const e = toDateTime(item.end)?.endOf('day')
        if (s && e && dt >= s && dt <= e) { return true }
      }
    }
  } else if (props.disabledDates?.start || props.disabledDates?.end) {
    const s = toDateTime(props.disabledDates.start)?.startOf('day')
    const e = toDateTime(props.disabledDates.end)?.endOf('day')
    if (s && e && dt >= s && dt <= e) { return true }
  }

  // availableDates: pokud je definováno, pak není dostupný každý, ale jen uvedené
  if (props.availableDates) {
    let available = false
    if (typeof props.availableDates === 'function') {
      available = !!props.availableDates(dt.toJSDate())
    } else if (Array.isArray(props.availableDates)) {
      for (const item of props.availableDates) {
        if (typeof item === 'string') {
          if (d === item) { available = true; break }
        } else if (item?.start || item?.end) {
          const s = toDateTime(item.start)?.startOf('day')
          const e = toDateTime(item.end)?.endOf('day')
          if (s && e && dt >= s && dt <= e) { available = true; break }
        }
      }
    } else if (props.availableDates?.start || props.availableDates?.end) {
      const s = toDateTime(props.availableDates.start)?.startOf('day')
      const e = toDateTime(props.availableDates.end)?.endOf('day')
      if (s && e && dt >= s && dt <= e) { available = true }
    }
    if (!available) { return true }
  }

  return false
}

// Attributes (subset): podpora dot/highlight přes dates
function getAttributesForDate(dt) {
  const iso = dt.toISODate()
  const out = []
  for (const attr of props.attributes) {
    if (!attr) { continue }
    const matched = matchDatesSpec(attr.dates, dt)
    if (matched) {
      out.push(attr)
    }
  }
  return out
}
function matchDatesSpec(spec, dt) {
  if (!spec) { return false }
  if (typeof spec === 'string') {
    return dt.toISODate() === spec
  }
  if (Array.isArray(spec)) {
    return spec.some(s => matchDatesSpec(s, dt))
  }
  if (spec.start || spec.end) {
    const s = toDateTime(spec.start)?.startOf('day')
    const e = toDateTime(spec.end)?.endOf('day')
    if (s && e && dt >= s && dt <= e) { return true }
  }
  return false
}

// Price map
function getPriceMeta(iso) {
  const meta = props.priceMap?.[iso]
  if (!meta) { return null }
  return {
    amount: meta.amount,
    currency: meta.currency || props.defaultCurrency,
    color: meta.color,
    note: meta.note,
  }
}
function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat(localeResolved.value === 'cs' ? 'cs-CZ' : 'en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

// Generování mřížky dnů pro jeden měsíc
function buildMonthGrid(monthStart) {
  const firstDow = props.firstDayOfWeek // 1=Mon
  const startOfMonth = monthStart.startOf('month')
  const endOfMonth = monthStart.endOf('month')

  // Najdi začátek mřížky (první den týdne před 1. dnem měsíce)
  let gridStart = startOfMonth
  while (gridStart.weekday !== ((firstDow % 7) || 7)) {
    gridStart = gridStart.minus({ days: 1 })
  }

  const weeks = []
  let cursor = gridStart
  for (let w = 0; w < 6; w++) {
    const row = []
    for (let d = 0; d < 7; d++) {
      const day = cursor
      const inMonth = day >= startOfMonth && day <= endOfMonth
      row.push({
        dt: day,
        iso: day.toISODate(),
        label: String(day.day),
        inMonth,
        isToday: day.hasSame(today, 'day'),
        isDisabled: isDisabledDate(day),
        attributes: getAttributesForDate(day),
      })
      cursor = cursor.plus({ days: 1 })
    }
    weeks.push(row)
    // Pokud jsme už celý měsíc vykreslili a poslední týden přesahuje, můžeme skončit
    if (cursor > endOfMonth && cursor.weekday === ((firstDow % 7) || 7)) {
      break
    }
  }

  return weeks
}

// Týdenní pohled: vezmeme týden kolem vybraného dne, jinak kolem baseMonth
const referenceDateForWeek = computed(() => {
  if (props.isRange && selection.value?.start) {
    return toDateTime(selection.value.start) ?? baseMonth.value
  }
  if (!props.isRange && selection.value) {
    return toDateTime(selection.value) ?? baseMonth.value
  }
  return baseMonth.value
})
function buildWeekGrid(centerDate) {
  const firstDow = props.firstDayOfWeek
  let weekStart = centerDate.startOf('week')
  // Luxon: startOf('week') respektuje locale; zajistíme posun na firstDayOfWeek
  while (weekStart.weekday !== ((firstDow % 7) || 7)) {
    weekStart = weekStart.minus({ days: 1 })
  }
  const row = []
  let cursor = weekStart
  for (let d = 0; d < 7; d++) {
    row.push({
      dt: cursor,
      iso: cursor.toISODate(),
      label: String(cursor.day),
      inMonth: true, // v weekly režimu neřešíme inMonth
      isToday: cursor.hasSame(today, 'day'),
      isDisabled: isDisabledDate(cursor),
      attributes: getAttributesForDate(cursor),
    })
    cursor = cursor.plus({ days: 1 })
  }
  return [row]
}

// Navigace mezi měsíci
function prev() {
  if (effectiveView.value === 'week') {
    baseMonth.value = baseMonth.value.minus({ weeks: 1 })
  } else {
    baseMonth.value = baseMonth.value.minus({ months: 1 })
  }
}
function next() {
  if (effectiveView.value === 'week') {
    baseMonth.value = baseMonth.value.plus({ weeks: 1 })
  } else {
    baseMonth.value = baseMonth.value.plus({ months: 1 })
  }
}

// Výběr dne
function daySelectable(day) {
  return !day.isDisabled
}
function handleDayClick(day) {
  if (!daySelectable(day)) {
    return
  }
  const dateObj = day.dt
  if (!props.isRange) {
    selection.value = dateObj.toISODate()
  } else {
    const cur = selection.value || { start: null, end: null }
    if (!cur.start || (cur.start && cur.end)) {
      selection.value = { start: dateObj.toISODate(), end: null }
    } else {
      const start = toDateTime(cur.start)
      if (dateObj < start) {
        selection.value = { start: dateObj.toISODate(), end: start.toISODate() }
      } else {
        selection.value = { start: start.toISODate(), end: dateObj.toISODate() }
      }
    }
  }
  emit('dayclick', { date: dateObj.toJSDate(), iso: dateObj.toISODate() })
}
function isSelected(day) {
  if (!selection.value) { return false }
  if (!props.isRange) {
    return selection.value === day.iso
  }
  const s = selection.value.start ? toDateTime(selection.value.start) : null
  const e = selection.value.end ? toDateTime(selection.value.end) : null
  if (s && !e) { return day.dt.hasSame(s, 'day') }
  if (s && e) { return day.dt >= s.startOf('day') && day.dt <= e.endOf('day') }
  return false
}
function isRangeEdge(day) {
  if (!props.isRange || !selection.value) { return false }
  const s = selection.value.start ? toDateTime(selection.value.start) : null
  const e = selection.value.end ? toDateTime(selection.value.end) : null
  return (s && day.dt.hasSame(s, 'day')) || (e && day.dt.hasSame(e, 'day'))
}

// Sloty
const hasDaySlot = computed(() => !!slots['day-content'])
const hasHeaderTitleSlot = computed(() => !!slots['header-title'])

// Výpočet panelů dle columns
const monthPanels = computed(() => {
  const arr = []
  for (let i = 0; i < Math.max(1, props.columns); i++) {
    arr.push(baseMonth.value.plus({ months: i }))
  }
  return arr
})

function monthLabel(dt) {
  return dt.setLocale(localeResolved.value).toFormat(monthTitleMask.value)
}

// Barvy – CSS custom prop
const colorStyle = computed(() => ({
  '--cal-accent': `var(--tw-colors-${props.color}-500, #3b82f6)`,
}))

function onDayMouseEnter(day) {
  emit('daymouseenter', { date: day.dt.toJSDate(), iso: day.iso })
}
function onDayMouseLeave(day) {
  emit('daymouseleave', { date: day.dt.toJSDate(), iso: day.iso })
}
</script>

<template>
  <div class="calendar-picker flex flex-col gap-3" :style="colorStyle" v-bind="attrs">
    <!-- Header -->
    <div v-if="titlePosition !== 'hidden'" class="flex items-center gap-2">
      <button type="button" class="px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700"
              @click="prev" aria-label="Previous">
        <i class="pi pi-chevron-left"></i>
      </button>

      <div class="flex-1">
        <div v-if="hasHeaderTitleSlot">
          <slot name="header-title" :base-month="baseMonth" :view="effectiveView" />
        </div>
        <div v-else class="flex"
             :class="{
               'justify-start': titlePosition === 'left',
               'justify-center': titlePosition === 'center',
               'justify-end': titlePosition === 'right',
             }">
          <div class="font-semibold">
            <template v-if="effectiveView === 'week'">
              {{ monthLabel(referenceDateForWeek) }}
            </template>
            <template v-else>
              <span v-for="(m,i) in monthPanels" :key="i" class="inline-block"
                    :class="i > 0 ? 'ml-4' : ''">
                {{ monthLabel(m) }}
              </span>
            </template>
          </div>
        </div>
      </div>

      <button type="button" class="px-2 py-1 rounded border border-neutral-300 dark:border-neutral-700"
              @click="next" aria-label="Next">
        <i class="pi pi-chevron-right"></i>
      </button>

      <div v-if="enableWeekToggle" class="ml-2 flex gap-1">
        <button type="button"
                class="px-2 py-1 rounded border text-sm"
                :class="effectiveView === 'month'
                  ? 'text-white border-transparent'
                  : 'border-neutral-300 dark:border-neutral-700'"
                :style="effectiveView === 'month' ? { background: 'var(--cal-accent)' } : {}"
                @click="internalView = 'month'">
          Měsíc
        </button>
        <button type="button"
                class="px-2 py-1 rounded border text-sm"
                :class="effectiveView === 'week'
                  ? 'text-white border-transparent'
                  : 'border-neutral-300 dark:border-neutral-700'"
                :style="effectiveView === 'week' ? { background: 'var(--cal-accent)' } : {}"
                @click="internalView = 'week'">
          Týden
        </button>
      </div>
    </div>

    <!-- Body -->
    <div v-if="effectiveView === 'week'" class="grid gap-4">
      <div class="border rounded-lg dark:border-neutral-700 overflow-hidden">
        <!-- Week header -->
        <div class="grid grid-cols-7 text-xs uppercase tracking-wide bg-neutral-50 dark:bg-neutral-800 px-2 py-1">
          <div v-for="d in 7" :key="'wh-'+d" class="text-center">
            {{ Info.weekdays('short', { locale: localeResolved })[(props.firstDayOfWeek + d - 1) % 7] }}
          </div>
        </div>
        <!-- Week row -->
        <div class="grid grid-cols-7">
          <div v-for="day in buildWeekGrid(referenceDateForWeek)[0]" :key="day.iso"
               class="relative h-[88px] border-t border-neutral-200 dark:border-neutral-700"
               :class="'border-r last:border-r-0 border-neutral-200 dark:border-neutral-700'">
            <button type="button"
                    class="w-full h-full px-1 py-1 text-left"
                    :class="[
                      day.isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                      isSelected(day) ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-neutral-900' : '',
                      day.isToday ? 'bg-blue-50 dark:bg-neutral-800' : ''
                    ]"
                    @mouseenter="onDayMouseEnter(day)"
                    @mouseleave="onDayMouseLeave(day)"
                    @click="handleDayClick(day)">
              <template v-if="$slots['day-content']">
                <slot name="day-content" :day="day" />
              </template>
              <template v-else>
                <div class="flex flex-col items-start gap-1">
                  <div class="flex items-center gap-1">
                    <span class="text-xs font-medium">{{ day.label }}</span>
                    <!-- Dot z attributes -->
                    <span v-for="(attr, ai) in day.attributes" :key="'ad'+ai">
                      <span v-if="attr.dot"
                            class="inline-block w-1.5 h-1.5 rounded-full"
                            :style="{ background: typeof attr.dot === 'string' ? attr.dot : 'var(--cal-accent)' }"></span>
                    </span>
                  </div>
                  <!-- Price badge -->
                  <div v-if="getPriceMeta(day.iso)" class="text-[10px] px-1 py-0.5 rounded"
                       :style="{ background: getPriceMeta(day.iso).color || 'rgba(59,130,246,0.1)' }"
                       :title="getPriceMeta(day.iso).note || ''">
                    {{ formatCurrency(getPriceMeta(day.iso).amount, getPriceMeta(day.iso).currency) }}
                  </div>
                </div>
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="grid gap-4" :style="{ gridTemplateColumns: `repeat(${Math.max(1, columns)}, minmax(0, 1fr))` }">
      <div v-for="(month, mi) in monthPanels" :key="'m'+mi" class="border rounded-lg dark:border-neutral-700 overflow-hidden">
        <!-- Month header -->
        <div class="grid grid-cols-7 text-xs uppercase tracking-wide bg-neutral-50 dark:bg-neutral-800 px-2 py-1">
          <div v-for="d in 7" :key="'mh-'+d" class="text-center">
            {{ Info.weekdays('short', { locale: localeResolved })[(props.firstDayOfWeek + d - 1) % 7] }}
          </div>
        </div>
        <!-- Month body -->
        <div class="grid grid-rows-6">
          <div v-for="(week, wi) in buildMonthGrid(month)" :key="'w'+wi" class="grid grid-cols-7">
            <div v-for="day in week" :key="day.iso"
                 class="relative h-[88px] border-t border-neutral-200 dark:border-neutral-700"
                 :class="[
                   'border-r last:border-r-0 border-neutral-200 dark:border-neutral-700',
                   day.inMonth ? '' : 'bg-neutral-50 dark:bg-neutral-900/30'
                 ]">
              <button type="button"
                      class="w-full h-full px-1 py-1 text-left"
                      :class="[
                        day.isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                        isSelected(day) ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-neutral-900' : '',
                        day.isToday ? 'bg-blue-50 dark:bg-neutral-800' : ''
                      ]"
                      @mouseenter="onDayMouseEnter(day)"
                      @mouseleave="onDayMouseLeave(day)"
                      @click="handleDayClick(day)">
                <template v-if="$slots['day-content']">
                  <slot name="day-content" :day="day" />
                </template>
                <template v-else>
                  <div class="flex flex-col items-start gap-1">
                    <div class="flex items-center gap-1">
                      <span class="text-xs font-medium">{{ day.label }}</span>
                      <!-- Dot z attributes -->
                      <span v-for="(attr, ai) in day.attributes" :key="'am'+ai">
                        <span v-if="attr.dot"
                              class="inline-block w-1.5 h-1.5 rounded-full"
                              :style="{ background: typeof attr.dot === 'string' ? attr.dot : 'var(--cal-accent)' }"></span>
                      </span>
                    </div>
                    <!-- Highlight (jemné pozadí) -->
                    <div v-for="(attr, hi) in day.attributes" :key="'hl'+hi">
                      <div v-if="attr.highlight"
                           class="absolute inset-0 -z-10"
                           :style="{
                             background: typeof attr.highlight === 'string'
                               ? attr.highlight
                               : (attr.highlight?.color || 'transparent'),
                             opacity: (attr.highlight?.fillMode ? 0.25 : 0.12)
                           }"></div>
                    </div>
                    <!-- Price badge -->
                    <div v-if="getPriceMeta(day.iso)" class="text-[10px] px-1 py-0.5 rounded"
                         :style="{ background: getPriceMeta(day.iso).color || 'rgba(59,130,246,0.1)' }"
                         :title="getPriceMeta(day.iso).note || ''">
                      {{ formatCurrency(getPriceMeta(day.iso).amount, getPriceMeta(day.iso).currency) }}
                    </div>
                  </div>
                </template>

                <!-- Range edge indikace -->
                <span v-if="isRangeEdge(day)"
                      class="pointer-events-none absolute inset-0 ring-2 ring-blue-500"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.calendar-picker :where(button) { transition: background 0.15s ease-in-out, box-shadow 0.15s ease-in-out; }
</style>
